/** @jsx jsx */
/* eslint-disable max-len, no-nested-ternary, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import { css, jsx } from '@emotion/core';
import moment from 'moment';
import OrganizationFilteredTree from 'components/organismos/organization/OrganizationFilteredTree/OrganizationFilteredTree';
import {
  BoardData, ViewType, BUSINESS_ID_NONE, CUSTOM_RANGE_MAX_DAYS,
} from 'domain/attend/shiftBoard/types';
import {
  DOW_LABELS, ViewRange, daysBetweenInclusive, parseDateStr,
} from 'domain/attend/shiftBoard/time';
import { HIDE_SUBMISSIONS } from 'domain/attend/shiftBoard/phase';
import { mock, mockButton, mockPrimaryButton } from './styles';
import BoardDatePicker from './BoardDatePicker';

const VIEWS: Array<{ value: ViewType; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '週' },
  { value: 'half', label: '半月' },
  { value: 'month', label: '1ヶ月' },
  { value: 'custom', label: '期間指定' },
];

const styles = {
  filters: css({
    // 組織・業務のドロップダウンをシフト表の固定ヘッダーより前面に出す（既存画面の Filters と同じ手当て）
    position: 'relative',
    zIndex: 30,
    margin: '16px 16px 0',
    padding: '12px 16px',
    background: '#fff',
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    display: 'flex',
    // 店舗名の行と業務の行を同じ高さに揃える
    alignItems: 'flex-end',
    gap: '20px',
    flexWrap: 'wrap',
  }),
  field: css({
    display: 'flex',
alignItems: 'center',
gap: '8px',
minHeight: '36px',
    // 組織選択の部品が上下に 1rem の余白を持つため、下端を合わせる
    paddingBottom: '16px',
  }),
  fieldLabel: css({
    fontSize: '13px', fontWeight: 700, color: mock.textMain, whiteSpace: 'nowrap',
  }),
  posBox: css({
    height: '36px',
minWidth: '240px',
border: `1px solid ${mock.gray10}`,
background: '#fff',
borderRadius: '4px',
    display: 'flex',
alignItems: 'center',
gap: '6px',
padding: '0 28px 0 6px',
cursor: 'pointer',
position: 'relative',
  }),
  customRange: css({
    display: 'flex', alignItems: 'center', gap: '6px', flex: 'none',
  }),
  posPlaceholder: css({
    fontSize: '13px', color: mock.textSub, whiteSpace: 'nowrap',
  }),
  posChip: css({
    display: 'inline-flex',
alignItems: 'center',
gap: '4px',
background: mock.primaryM95,
border: `1px solid ${mock.primaryM80}`,
    borderRadius: '4px',
fontSize: '12px',
padding: '3px 6px',
whiteSpace: 'nowrap',
  }),
  posMenu: css({
    position: 'absolute',
top: '40px',
left: 0,
zIndex: 50,
background: '#fff',
border: `1px solid ${mock.gray10}`,
borderRadius: '4px',
    boxShadow: '0 3px 8px 0 rgba(0,0,0,.16)',
minWidth: '220px',
padding: '6px 0',
  }),
  posItem: css({
    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', cursor: 'pointer', '&:hover': { background: mock.primaryM95 },
  }),
  head: css({
    padding: '12px 16px 0',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignContent: 'flex-start',
    gap: '12px',
    // 9/16 に「期間指定」の日付入力ぶんの高さ（96px）を先に空けていたが、上に余白が出ていたので外した（9/25）。
    // 日付入力は表示単位と同じ行に並ぶので、切り替えても高さはほとんど変わらない
  }),
  // 右側の操作ボタン。まとまりで右に寄せ、幅が足りないときはまとまりごと次の行へ折り返す
  actions: css({
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginLeft: 'auto',
  }),
  title: css({ fontSize: '20px', fontWeight: 700, whiteSpace: 'nowrap' }),
  sub: css({
    fontSize: '12px', color: mock.textSub, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }),
  units: css({ display: 'flex', flex: 'none' }),
  unit: css({
    border: `1px solid ${mock.gray20}`,
background: '#fff',
color: mock.textMain,
fontSize: '12px',
fontWeight: 700,
padding: '7px 12px',
    cursor: 'pointer',
marginLeft: '-1px',
whiteSpace: 'nowrap',
flex: 'none',
fontFamily: 'inherit',
    '&[data-on="true"]': { background: mock.primary, color: '#fff', borderColor: mock.primary },
  }),
  pager: css({
    border: `1px solid ${mock.gray20}`, background: '#fff', color: mock.textMain, borderRadius: '5px', fontSize: '12px', padding: '6px 9px', cursor: 'pointer', flex: 'none',
  }),
};

export type ToolbarProps = {
  orgCode: string;
  setOrgCode: (code: string) => void;
  view: ViewType;
  setView: (v: ViewType) => void;
  range: ViewRange;
  moveRange: (direction: 1 | -1) => void;
  setAnchor: (d: string) => void;
  board: BoardData | null;
  businessIds: Array<string>;
  setBusinessIds: (ids: Array<string>) => void;
  onAutoGenerate: () => void;
  onCopy: () => void;
  onPrint: () => void;
  onCsv: () => void;
  onRequiredStaff: () => void;
  onAddSupport: () => void;
  onConfirm: () => void;
  /** ロック解除（A8 確定解除） */
  onUnlock: () => void;
  onSubmissions: () => void;
  /** 任意期間の指定 */
  setCustomRange: (from: string, to: string) => void;
};

const md = (s: string): string => {
  const d = parseDateStr(s);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * モック準拠の見出し（組織・業務フィルタ／タイトル・期間・状態／表示単位・ページャ・操作）
 */
const Toolbar: React.FC<ToolbarProps> = ({
  orgCode, setOrgCode, view, setView, range, moveRange, setAnchor, board, businessIds, setBusinessIds,
  onAutoGenerate, onCopy, onPrint, onCsv, onRequiredStaff, onAddSupport, onConfirm, onUnlock, onSubmissions,
  setCustomRange,
}) => {
  const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';
  const [posOpen, setPosOpen] = useState(false);
  const role = board ? board.role : null;
  const editable = !!(role && role.editable);
  const canConfirm = !!(role && role.canConfirm);
  const confirmStatus = board ? board.period.confirmStatus : 'NONE';
  const businesses = board ? board.businesses.filter((b) => !b.isBreak) : [];
  const selectedNames = businessIds.map((id) => (id === BUSINESS_ID_NONE ? '未設定' : (businesses.filter((b) => b.businessId === id)[0] || { businessName: id }).businessName));
  // 「未設定」は「全て」の直下に固定し、業務と同時に選べるようにする（9/9 決定）
  const options = [{ id: '', name: '全て' }, { id: BUSINESS_ID_NONE, name: '未設定' }, ...businesses.map((b) => ({ id: b.businessId, name: b.businessName }))];
  const periodName = board && board.period.name ? board.period.name : `${parseDateStr(range.from).getMonth() + 1}月${parseDateStr(range.from).getDate() <= 15 ? '前半' : '後半'}`;
  const statusLabel = confirmStatus === 'CONFIRMED' ? '公開済み（ロック中）' : confirmStatus === 'PARTIAL' ? '一部公開済み（公開した日はロック中）' : '下書き（未公開）';
  const spanDays = daysBetweenInclusive(range.from, range.to);
  const overLimit = view === 'custom' && spanDays > CUSTOM_RANGE_MAX_DAYS;
  const rangeLabel = range.from === range.to
    ? `${md(range.from)}（${DOW_LABELS[parseDateStr(range.from).getDay()]}）`
    : `${md(range.from)}〜${md(range.to)}`;

  return (
    <div>
      <div css={styles.filters}>
        <div style={{ flex: '1 1 560px', minWidth: '280px' }}>
          <OrganizationFilteredTree
            functionType={2}
            staffCode={loginStaffCode}
            orgLabel=""
            initOrgValue={orgCode}
            orgCallback={(args: any) => {
              const code = Array.isArray(args) ? args[0] : args;
              if (code && code !== orgCode) {
                setOrgCode(String(code));
              }
            }}
            targetdayForm={moment(range.from).toDate()}
            targetdayTo={moment(range.to).toDate()}
            addAllItem={false}
            showSelectOrg={true}
            isAlignItemsCenter={true}
          />
        </div>
        <div css={styles.field}>
          <span css={styles.fieldLabel}>業務</span>
          <div style={{ position: 'relative' }}>
            <div css={styles.posBox} onClick={() => setPosOpen(!posOpen)} style={{ borderColor: posOpen ? mock.primary : mock.gray10 }}>
              {businessIds.length === 0 && <span css={styles.posPlaceholder}>全て</span>}
              {selectedNames.slice(0, 1).map((n) => (
                <span key={n} css={styles.posChip}>
                  {n}
                  <span style={{ color: mock.textSub, fontSize: '11px' }} onClick={(e) => { e.stopPropagation(); setBusinessIds(businessIds.slice(1)); }}>×</span>
                </span>
            ))}
              {selectedNames.length > 1 && <span css={styles.posChip} style={{ color: mock.textSub }}>{`+${selectedNames.length - 1}`}</span>}
              <span style={{
 position: 'absolute', right: '10px', color: mock.gray40, fontSize: '10px',
}}
              >
                ▼
              </span>
            </div>
            {posOpen && (
            <div css={styles.posMenu}>
              {options.map((o) => {
                const on = o.id === '' ? businessIds.length === 0 : businessIds.indexOf(o.id) !== -1;
                return (
                  <div
                    key={o.id || 'all'}
                    css={styles.posItem}
                    onClick={() => {
                      if (o.id === '') {
                        setBusinessIds([]);
                        return;
                      }
                      const next = on ? businessIds.filter((x) => x !== o.id) : [...businessIds, o.id];
                      // 全部選んだ状態は「全て」と同じなので空に戻す
                      setBusinessIds(next.length === options.length - 1 ? [] : next);
                    }}
                  >
                    <span style={{
                      width: '16px', height: '16px', border: `1px solid ${on ? mock.primary : mock.gray20}`, borderRadius: '3px', background: on ? mock.primary : '#fff', color: '#fff', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                    }}
                    >
                      {on ? '✓' : ''}
                    </span>
                    <span style={{ fontWeight: on ? 700 : 400 }}>{o.name}</span>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>

      <div css={styles.head}>
        <div style={{ flex: '1 1 220px', minWidth: 0, overflow: 'hidden' }}>
          <div css={styles.title}>シフト編成</div>
          <div css={styles.sub}>{`${periodName}（${md(range.from)}〜${md(range.to)}）/ ${statusLabel}`}</div>
        </div>
        <div style={{
 display: 'flex', alignItems: 'center', gap: '8px', flex: 'none', whiteSpace: 'nowrap',
}}
        >
          <div css={styles.units}>
            {VIEWS.map((v) => (
              <button key={v.value} type="button" css={styles.unit} data-on={view === v.value} onClick={() => setView(v.value)}>{v.label}</button>
            ))}
          </div>
          <div style={{
 display: 'flex', alignItems: 'center', gap: '4px', flex: 'none',
}}
          >
            <button type="button" css={styles.pager} onClick={() => moveRange(-1)}>‹</button>
            <span style={{ fontSize: '12px', color: mock.textSub, whiteSpace: 'nowrap' }}>{rangeLabel}</span>
            <button type="button" css={styles.pager} onClick={() => moveRange(1)}>›</button>
            <button type="button" css={styles.pager} onClick={() => setAnchor(moment().format('YYYY-MM-DD'))} title="今日">今日</button>
          </div>
          {view === 'custom' && (
          <div css={styles.customRange}>
            {/* 月次帳票などと同じカレンダー（10/6）。高さは表示単位のボタンと同じ 32px */}
            <BoardDatePicker id="range-from" value={range.from} max={range.to} height={32} onChange={(v) => setCustomRange(v, range.to)} />
            <span style={{ color: mock.textSub, fontSize: '12px' }}>〜</span>
            <BoardDatePicker id="range-to" value={range.to} min={range.from} height={32} onChange={(v) => setCustomRange(range.from, v)} />
            <span style={{ fontSize: '11px', color: overLimit ? mock.secondary : mock.textSub, whiteSpace: 'nowrap' }}>
              {overLimit ? `${CUSTOM_RANGE_MAX_DAYS}日までにしてください` : `${spanDays}日間`}
            </span>
          </div>
          )}
        </div>
        <div css={styles.actions}>
          {editable && <button type="button" css={mockButton} onClick={onCopy}>前期間からコピー</button>}
          {editable && <button type="button" css={mockButton} onClick={onAutoGenerate}>スタッフの曜日設定から作成</button>}
          {editable && <button type="button" css={mockButton} onClick={onAddSupport}>支援追加</button>}
          <button type="button" css={mockButton} onClick={onRequiredStaff}>予定人数</button>
          {/* 提出状況はフェーズ 1 では出さない（9/17 決定）。実装は残してある */}
          {!HIDE_SUBMISSIONS && board && board.role.canViewSubmissions && <button type="button" css={mockButton} onClick={onSubmissions}>提出状況</button>}
          {role && role.canDownload && <button type="button" css={mockButton} onClick={onCsv}>CSV出力</button>}
          {role && role.canPrint && <button type="button" css={mockButton} onClick={onPrint}>印刷</button>}
          {/*
            公開した日はロックする（9/24 決定）。すべて公開済みなら公開ボタンがロック解除ボタンに変わる。
            一部だけ公開済みのときは、残りを公開するボタンとロック解除の両方を出す
          */}
          {canConfirm && confirmStatus !== 'NONE' && (
            <button type="button" css={confirmStatus === 'CONFIRMED' ? mockPrimaryButton : mockButton} onClick={onUnlock}>ロック解除</button>
          )}
          {canConfirm && confirmStatus !== 'CONFIRMED' && (
            <button type="button" css={mockPrimaryButton} onClick={onConfirm}>確定して公開</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;
